import { Inject, inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { restartListenerSessions } from '@shared/operators/listener-session';
import { BaseRevisioneFormService } from '../abstracts/base-revisione-form.service';
import { REVISIONE_FORM_SERVICES } from './consts/form-orchestrator.const';
import { Revisione53State } from '../store/revisione53.reducers';
import { Revisione53CoreActions } from '../store/revisione53.actions';
import { MyVerbale } from '../models/interfaces/revisione.interface';
import { collectInfoPathAttachmentPaths, decodeInfoPathAttachment, parseVerbaleFileToMyVerbale } from '../helpers/xml-to-verbale.helper';
import { ATTACHMENT_PLACEHOLDER_B64 } from '../helpers/verbale-attachment-placeholder';
import { isBilancioAllegatoPath, setNestedValue } from '../helpers/infopath-attachment.constants';
import { IndexedDBService } from '@core/services/indexeddb.service';
import { IDbAllegato } from '@core/models/interfaces/indexeddb.interface';
import { Revisione53CoreSelectors } from '../store/selectors/core.selectors';
import { computeChangedSections } from './form-orchestrator.types';
import { SezIIAccertamentoFormService } from '../forms/sez-II-accertamento/sez-ii-accertamento-form.service';
import { FormSettlingService } from './form-settling.service';
import { setHiddenRequiredBilancioDefaults } from '../forms/dati-bilancio-bis/helpers/dati-bilancio-bis-defaults.helper';
import { restoreImportOverwrittenFields } from '../helpers/import-overwritten-fields.helper';

@Injectable()
export class FormOrchestratorService implements OnDestroy {
    private store = inject<Store<Revisione53State>>(Store);
    private indexDbService = inject(IndexedDBService);
    private settlingService = inject(FormSettlingService);
    private readonly sectionMap = new Map<string, BaseRevisioneFormService[]>();
    private prevVerbale: MyVerbale | null = null;
    private _started = false;
    private _listenersInitialized = false;

    private subs = new Subscription();
    constructor(
        @Optional()
        @Inject(REVISIONE_FORM_SERVICES)
        private formServices: BaseRevisioneFormService[] = []
    ) {}

    initAllListeners(): void {
        if (this._listenersInitialized) {
            // Nuovo verbale senza F5: form ripopolati in silenzio dallo store, memoria degli operatori "on change" azzerata.
            this.formServices.forEach((service) => service.refreshFormData(false));
            restartListenerSessions();
            return;
        }
        this._listenersInitialized = true;
        this.formServices.forEach((service) => service.initListener());
    }

    refreshAll(): void {
        this.formServices.forEach((service) => service.refreshFormData());
    }

    /**
     * Synchronously flushes every section form's current value to the store, bypassing the debounced writers.
     * SezII (accertamento) is out of scope and intentionally excluded.
     */
    flushAllToStore(): void {
        this.formServices.filter((service) => !(service instanceof SezIIAccertamentoFormService)).forEach((service) => service.flushToStore());
    }

    /**
     * Reads a verbale XML file, replaces every InfoPath attachment with an IndexedDB-backed file
     * (real Bilancio kept as-is, every other attachment replaced with the placeholder PDF), then
     * dispatches the resulting MyVerbale to the NgRx store and refreshes all forms.
     *
     * The imported verbale fully replaces the current one (no merge).
     */
    importXmlAndRefresh(file: File): Observable<string> {
        return parseVerbaleFileToMyVerbale(file).pipe(
            switchMap(({ verbale, solutionName }) =>
                this._saveAttachmentsAndPatch(verbale).pipe(
                    tap((patched) => {
                        const datiBilancio = patched.my_SezioneI__Rilevazione?.my_DatiBilancio;
                        if (datiBilancio) {
                            setHiddenRequiredBilancioDefaults(datiBilancio);
                        }
                        this.store.dispatch(Revisione53CoreActions.updateMyVerbale({ my_Verbale: patched }));
                        this.refreshAll();
                        // Re-enter the navbar loading state while the imported verbale is being validated.
                        this.settlingService.notifyBulkRefresh();
                        this._restoreImportOverwrittenFields(patched);
                    }),
                    map(() => solutionName)
                )
            )
        );
    }

    /**
     * Once the import has settled (listeners done recomputing), puts back the XML value of the fields listed in
     * `restoreImportOverwrittenFields`. Silent refresh: the restored value must not re-trigger the listeners that overwrote it.
     */
    private _restoreImportOverwrittenFields(imported: MyVerbale): void {
        this.subs.add(
            this.settlingService.settling$
                .pipe(
                    filter((settling) => !settling),
                    take(1),
                    switchMap(() => this.store.select(Revisione53CoreSelectors.getMyVerbale).pipe(take(1)))
                )
                .subscribe((current) => {
                    const restored = cloneDeep(current);
                    if (!restoreImportOverwrittenFields(restored, imported)) return;
                    this.store.dispatch(Revisione53CoreActions.updateMyVerbale({ my_Verbale: restored }));
                    this.formServices.forEach((service) => service.refreshFormData(false));
                })
        );
    }

    start(): void {
        if (this._started) return;
        this._started = true;

        this.formServices
            .filter((s) => s.dependencyPaths.size > 0)
            .forEach((s) =>
                s.dependencyPaths.forEach((path) => {
                    if (!this.sectionMap.has(path)) this.sectionMap.set(path, []);
                    this.sectionMap.get(path)!.push(s);
                })
            );

        this.subs.add(
            this.store
                .select(Revisione53CoreSelectors.getMyVerbale)
                .pipe(filter((v): v is MyVerbale => !!v))
                .subscribe((curr) => {
                    if (!this.prevVerbale) {
                        this.prevVerbale = curr;
                        return;
                    }
                    const changedPaths = computeChangedSections(this.prevVerbale, curr, [...this.sectionMap.keys()]);
                    if (changedPaths.length > 0) {
                        changedPaths.forEach((path) => {
                            this.sectionMap.get(path)!.forEach((s) => s.refreshFormData());
                        });
                    }
                    this.prevVerbale = curr;
                })
        );
    }

    /** Imperative API: notifies all services registered on a path without waiting for a store emission. */
    notifyChange(sectionPath: string): void {
        this.sectionMap.get(sectionPath)?.forEach((s) => s.refreshFormData());
    }

    stop(): void {
        this.subs.unsubscribe();
        this.subs = new Subscription();
        this.sectionMap.clear();
        this.prevVerbale = null;
        this._started = false;
    }

    ngOnDestroy(): void {
        this.stop();
    }

    /**
     * For each InfoPath attachment in `parsedData`:
     *   - Bilancio path → save the decoded real file to IndexedDB (placeholder if decode fails)
     *   - any other path → save the placeholder file to IndexedDB
     *
     * Returns a clone of `parsedData` with every attachment base64 replaced by its new IndexedDB ID.
     */
    private _saveAttachmentsAndPatch(parsedData: Partial<MyVerbale>): Observable<MyVerbale> {
        const attachments = collectInfoPathAttachmentPaths(parsedData);
        if (attachments.length === 0) {
            return of(cloneDeep(parsedData) as MyVerbale);
        }

        const saves$ = attachments.map(({ path, value }) => this._saveAttachmentToIndexedDb(path, value).pipe(map((allegato) => ({ path, id: allegato.id }))));

        return forkJoin(saves$).pipe(
            map((results) => {
                const patched = cloneDeep(parsedData);
                for (const { path, id } of results) {
                    if (id) setNestedValue(patched as Record<string, unknown>, path, id);
                }
                return patched;
            })
        );
    }

    /** Saves the file underlying an InfoPath attachment to IndexedDB and returns the persisted record. */
    private _saveAttachmentToIndexedDb(path: string[], value: string): Observable<IDbAllegato> {
        const placeholderFile = decodeInfoPathAttachment(ATTACHMENT_PLACEHOLDER_B64)!;
        const fileToSave = isBilancioAllegatoPath(path) ? decodeInfoPathAttachment(value) ?? placeholderFile : placeholderFile;
        return this.indexDbService.add(fileToSave);
    }
}
